import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { SvgOutlinedSearch } from '@/icons/svg-outlined-search'
import { debounce } from '@/utils/functions/debounce'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantCitySearchFiltersInputProps {
    setHasValue?: (hasValue: boolean) => void
}

export const RestaurantCitySearchFiltersInput = ({
    setHasValue,
}: IRestaurantCitySearchFiltersInputProps) => {
    const t = useTranslations()

    const {
        query: { name },
        setters: { setName },
        fullCount,
    } = useRestaurantCitySearchContext()

    const [searchInput, setSearchInput] = useState(name ?? '')

    // Follow external query changes (initial url value, the empty state's
    // filter reset) without fighting the debounce: while the user is typing
    // the input is focused and must never be clobbered by lagging query echos
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        const externalName = name ?? ''

        const isTyping =
            typeof document !== 'undefined' && document.activeElement === inputRef.current

        if (isTyping) return

        setSearchInput((current) => {
            if (current === externalName) return current

            setHasValue?.(externalName !== '')

            return externalName
        })
    }, [name, setHasValue])

    const debouncedSetName = useMemo(() => debounce(setName, 150), [setName])

    const onInputChanged = useCallback(
        (nextValue: string) => {
            setSearchInput(nextValue)

            setHasValue?.(nextValue !== '')

            const isInitialValue = searchInput === '' && nextValue.length === 1

            // When the user types the first character, we want to fetch results immediately
            // This is to make the search feel more responsive and to let the user know that the search is working
            // without having to click a button
            if (isInitialValue) {
                setName(nextValue)
            } else {
                debouncedSetName(nextValue)
            }
        },
        [debouncedSetName, searchInput, setName, setHasValue]
    )

    return (
        <div className="relative w-full">
            {/* The Svg components ignore className, position via wrapper */}
            <span className="absolute left-4 top-1/2 -translate-y-1/2 flex pointer-events-none">
                <SvgOutlinedSearch color={COLORS.fg.tertiary} size={18} />
            </span>

            <input
                ref={inputRef}
                type="search"
                value={searchInput}
                onChange={(event) => onInputChanged(event.target.value)}
                aria-label={t('restaurants.city.searchPlaceholder')}
                placeholder={
                    typeof fullCount === 'number' && fullCount > 0
                        ? t('restaurants.city.searchPlaceholderWithCount', { count: fullCount })
                        : t('restaurants.city.searchPlaceholder')
                }
                className="h-12 w-full rounded-full border border-border-primary bg-bg-neutral-base pl-11 pr-4
                    text-p-sm text-fg-primary placeholder:text-fg-tertiary outline-none transition-colors
                    duration-150 focus:border-fg-primary"
            />
        </div>
    )
}
